// RequireAuth.tsx — gate for signed-in routes. While /api/me loads it shows a
// spinner; when signed out it renders the band-free sign-in card (C-CARD-1's
// auth-flow shape) inside .auth-wrapper and hands off to the server's OIDC
// login on click. Local-dev servers answer /api/me without a session, so the
// gate never shows there. RequireScanAccess is the viewer's variant: a visitor
// holding a share link for the scan passes through signed out, as a guest.
import type { ReactNode } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { adoptShareToken } from '../api';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Spinner from 'react-bootstrap/Spinner';
import { BsBoxArrowInRight } from 'react-icons/bs';
import type { AuthState } from '../hooks/useAuth';
import { redirectToLogin } from '../hooks/useAuth';

export function RequireAuth({ auth, children }: { auth: AuthState; children: ReactNode }) {
  if (auth.loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }
  if (auth.authenticated) return <>{children}</>;
  return (
    <div className="auth-wrapper">
      <Card style={{ width: '100%', maxWidth: '28rem' }}>
        <Card.Body className="p-4">
          <h2 className="mb-3">Log In</h2>
          <p className="text-body-secondary">
            Scans are private unless their owner shares them. Log in with your lab0 identity to see your library, scans shared with you, and to start scanning.
          </p>
          {auth.error && <p className="text-danger small">{auth.error}</p>}
          <Button variant="primary" onClick={redirectToLogin}>
            <BsBoxArrowInRight className="me-2" />
            Log In
          </Button>
        </Card.Body>
      </Card>
    </div>
  );
}

// RequireScanAccess gates /scans/:id: signed-in users pass as with
// RequireAuth, and so does anyone holding a share token for this scan (from
// ?share= or remembered for the session) — the server decides what they see.
export function RequireScanAccess({ auth, children }: { auth: AuthState; children: ReactNode }) {
  const { id = '' } = useParams();
  const { search } = useLocation();
  const token = id ? adoptShareToken(id, search) : '';
  if (token && !auth.loading) return <>{children}</>;
  return <RequireAuth auth={auth}>{children}</RequireAuth>;
}
